/**
 * Ficha de uma S.A. no mercado — tudo que a base pública da Receita sabe.
 *
 * Não confundir com /empresas/:id (a ficha do LEAD, dado nosso): esta ficha
 * existe para toda S.A. da Bahia, esteja ela na base de marketing ou não.
 * Quando está, o cabeçalho aponta para a ficha do lead — lá ficam jornada,
 * estágio e o botão de tornar cliente.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { api, type Item } from '../api/cliente'
import { podeGerirSistema, useAuth } from '../estado/auth'
import {
  Carregando,
  Cartao,
  Etiqueta,
  Tabela,
  Cabecalho,
  Vazio,
  formatarDataCurta,
} from '../componentes/ui'
import { formatarCnpjSa } from './MercadoSa'

const numero = (n: number): string => n.toLocaleString('pt-BR')

function reais(valor: number): string {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

interface Socio {
  nome?: string
  qualificacao?: string
  faixa_etaria?: string
  data_entrada?: string
}

interface Detalhe {
  cnpj: string
  razaoSocial: string
  nomeFantasia: string | null
  natureza: 'aberta' | 'fechada'
  naturezaJuridica: string
  situacaoCadastral: string
  dataSituacao: string | null
  motivoSituacao: string | null
  dataInicioAtividade: string | null
  capital: number
  matrizFilial: string
  cnaePrincipalCod: string
  cnaePrincipalDesc: string
  cnaesSecundarios: Array<{ codigo: string; descricao: string }>
  endereco: {
    logradouro: string
    numero: string
    complemento: string
    bairro: string
    cep: string
    municipio: string
    uf: string
  }
  email: string | null
  telefone: string | null
  socios: Socio[]
  qtdeSocios: number
  baseReferencia: string
  presenca: Array<{ uf: string; unidades: number }>
  filiaisNaBa: Array<{ cnpj: string; municipio: string; situacao: string }>
  lead: { id: string; estagio: string; organizacaoId: string | null } | null
  tabelaVigente: {
    descricao: string
    regraPreco: { modo?: string; valorUnitarioCentavos?: number } | null
    vigenciaInicio: string
    vigenciaFim: string | null
    atoAprovacao: string | null
  } | null
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
      <dt className="w-44 shrink-0 text-xs font-medium text-slate-500">{rotulo}</dt>
      <dd className="text-sm text-slate-800">{children}</dd>
    </div>
  )
}

/** Idade da companhia em anos completos, a partir da data da Receita. */
function idade(iso: string | null): string | null {
  if (!iso) return null
  const inicio = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(inicio.getTime())) return null
  const anos = Math.floor((Date.now() - inicio.getTime()) / (365.25 * 24 * 3600 * 1000))
  return anos >= 1 ? `${anos} anos` : 'menos de 1 ano'
}

export function EmpresaSa() {
  const { cnpj } = useParams<{ cnpj: string }>()
  const { usuario } = useAuth()
  // Mesmo critério da página do mercado: o enquadramento comercial fica com
  // quem gere o sistema, não com os perfis operacionais da EGBA.
  const veEnquadramento = podeGerirSistema(usuario?.papel)

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['mercado-sa-empresa', cnpj],
    queryFn: () => api.get<Item<Detalhe>>(`/api/v1/mercado-sa/empresas/${cnpj}`),
    enabled: !!cnpj,
    retry: 1,
  })

  if (isLoading) return <Carregando />
  if (isError || !r) {
    return (
      <Vazio
        titulo="S.A. não encontrada"
        descricao="O CNPJ não está na base pública da Receita ou a base está indisponível."
        acao={
          <Link to="/mercado-sa" className="text-sm font-medium text-marca-700 hover:underline">
            ← Voltar ao mercado de S.A.
          </Link>
        }
      />
    )
  }

  const e = r.dados
  const ativa = e.situacaoCadastral === 'Ativa'
  const anos = idade(e.dataInicioAtividade)
  const valorCm = e.tabelaVigente?.regraPreco?.valorUnitarioCentavos ?? null

  return (
    <div className="space-y-5">
      {/* ----------------------------------------------------------- cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/mercado-sa" className="text-xs font-medium text-marca-700 hover:underline">
            ← Mercado de S.A.
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{e.razaoSocial}</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {formatarCnpjSa(e.cnpj)}
            {e.nomeFantasia ? ` · ${e.nomeFantasia}` : ''}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Etiqueta tom={ativa ? 'sucesso' : 'atencao'}>{e.situacaoCadastral}</Etiqueta>
            <Etiqueta tom="marca">capital {e.natureza === 'aberta' ? 'aberto' : 'fechado'}</Etiqueta>
            <Etiqueta tom="neutro">{e.matrizFilial}</Etiqueta>
            {anos && <Etiqueta tom="neutro">{anos} de atividade</Etiqueta>}
          </div>
        </div>
        <div className="text-right">
          {e.lead ? (
            <Link
              to={`/empresas/${e.lead.id}`}
              className="inline-flex items-center rounded-lg bg-marca-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-marca-700"
            >
              {e.lead.organizacaoId ? 'Cliente — abrir ficha do lead' : 'Abrir na base de marketing'}
            </Link>
          ) : (
            <p className="max-w-52 text-xs text-slate-500">
              Fora da base de marketing — importe pela Prospecção para trabalhar esta companhia.
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ------------------------------------------------------------ cadastro */}
        <Cartao titulo="Cadastro na Receita" descricao={`Base pública de CNPJ · referência ${e.baseReferencia || '—'}.`}>
          <dl className="space-y-2.5">
            <Linha rotulo="Natureza jurídica">{e.naturezaJuridica}</Linha>
            <Linha rotulo="Capital social">
              <span className="font-medium">{reais(e.capital)}</span>
              <span className="ml-1 text-xs text-slate-500">(subscrito — não é faturamento)</span>
            </Linha>
            <Linha rotulo="Início de atividade">
              {formatarDataCurta(e.dataInicioAtividade)}
              {anos ? ` · ${anos}` : ''}
            </Linha>
            <Linha rotulo="Situação cadastral">
              {e.situacaoCadastral}
              {e.dataSituacao ? ` desde ${formatarDataCurta(e.dataSituacao)}` : ''}
              {!ativa && e.motivoSituacao ? ` — ${e.motivoSituacao}` : ''}
            </Linha>
            <Linha rotulo="Endereço">
              {[
                [e.endereco.logradouro, e.endereco.numero].filter(Boolean).join(', '),
                e.endereco.complemento,
                e.endereco.bairro,
                `${e.endereco.municipio}/${e.endereco.uf}`,
                e.endereco.cep ? `CEP ${e.endereco.cep}` : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </Linha>
            <Linha rotulo="E-mail no CNPJ">{e.email?.toLowerCase() ?? '—'}</Linha>
            <Linha rotulo="Telefone no CNPJ">{e.telefone ?? '—'}</Linha>
          </dl>
        </Cartao>

        {/* ----------------------------------------------------------- atividade */}
        <Cartao titulo="Atividade econômica" descricao="CNAE declarado à Receita.">
          <dl className="space-y-2.5">
            <Linha rotulo="Principal">
              {e.cnaePrincipalDesc} <span className="text-xs text-slate-500">({e.cnaePrincipalCod})</span>
            </Linha>
          </dl>
          {e.cnaesSecundarios.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-slate-500">
                Secundárias ({numero(e.cnaesSecundarios.length)})
              </p>
              <ul className="mt-1.5 max-h-48 space-y-1 overflow-y-auto text-xs text-slate-600">
                {e.cnaesSecundarios.map((c) => (
                  <li key={c.codigo}>
                    {c.descricao} <span className="text-slate-400">({c.codigo})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Cartao>

        {/* --------------------------------------------------- quadro societário */}
        <Cartao
          titulo={`Quadro societário (${numero(e.qtdeSocios)})`}
          descricao="Sócios e administradores declarados à Receita — o caminho para achar o decisor."
        >
          {e.socios.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum sócio na base pública.</p>
          ) : (
            <ul className="divide-y divide-[color:var(--color-borda)]">
              {e.socios.map((s, i) => (
                <li key={`${s.nome}-${i}`} className="flex flex-wrap items-baseline justify-between gap-x-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{s.nome ?? '—'}</p>
                    <p className="text-xs text-slate-500">
                      {[s.qualificacao, s.faixa_etaria].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {s.data_entrada && (
                    <p className="text-xs text-slate-500">desde {formatarDataCurta(s.data_entrada)}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Cartao>

        {/* -------------------------------------------------------------- valores */}
        <Cartao
          titulo="Valores de publicação"
          descricao="Tabela oficial vigente da categoria de balanços e balancetes."
        >
          {valorCm ? (
            <div className="space-y-2">
              <p className="text-2xl font-semibold tracking-tight text-slate-900">
                {(valorCm / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                <span className="text-sm font-normal text-slate-500"> por cm-coluna</span>
              </p>
              <p className="text-xs text-slate-500">
                {e.tabelaVigente?.descricao}
                {e.tabelaVigente?.atoAprovacao ? ` — ${e.tabelaVigente.atoAprovacao}` : ''} · vigente
                desde {formatarDataCurta(e.tabelaVigente?.vigenciaInicio)}
                {e.tabelaVigente?.vigenciaFim
                  ? ` até ${formatarDataCurta(e.tabelaVigente.vigenciaFim)}`
                  : ''}
              </p>
              <p className="rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
                O valor final depende da diagramação real (cm-coluna medidos no pedido).
                {veEnquadramento &&
                  ' Desde 2022 o órgão oficial não é exigido das S.A. em geral (Lei 6.404/76, art. 289) — a oferta é fé pública, comprovante e diagramação oficial no veículo do Estado.'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Sem tabela vigente para a categoria de balanços.</p>
          )}
        </Cartao>
      </div>

      {/* ------------------------------------------------------------- presença */}
      {(e.presenca.length > 1 || e.filiaisNaBa.length > 0) && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Cartao
            titulo="Presença por estado"
            descricao="Estabelecimentos ativos da mesma raiz de CNPJ em todo o país."
          >
            <div className="flex flex-wrap gap-2">
              {e.presenca.map((p) => (
                <span
                  key={p.uf}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-medium ${
                    p.uf === 'BA' ? 'bg-marca-50 text-marca-800' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {p.uf} · {numero(p.unidades)}
                </span>
              ))}
            </div>
          </Cartao>
          {e.filiaisNaBa.length > 0 && (
            <Cartao
              titulo={`Filiais ativas na Bahia (${numero(e.filiaisNaBa.length)})`}
              descricao="Outros estabelecimentos da mesma companhia no estado."
            >
              <Tabela>
                <Cabecalho colunas={['CNPJ', 'Município']} />
                <tbody>
                  {e.filiaisNaBa.map((f) => (
                    <tr key={f.cnpj} className="border-b border-[color:var(--color-borda)] last:border-0">
                      <td className="py-1.5 pr-3 text-xs tabular text-slate-600">{formatarCnpjSa(f.cnpj)}</td>
                      <td className="py-1.5 text-slate-700">{f.municipio}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
            </Cartao>
          )}
        </div>
      )}
    </div>
  )
}
